import '@mantine/core/styles.css';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';


import { AppShell, Burger, Group, useMantineColorScheme, ActionIcon, useComputedColorScheme, Container, Text, SegmentedControl, Overlay } from '@mantine/core';
import { useDisclosure, useLocalStorage, useMediaQuery } from '@mantine/hooks';
import NavigationItems from './components/NavigationItems';
// import '@mantine/notifications/styles.css';
import Login from './pages/Login';
import { InstallAppButton } from './components/InstallAppButton';
// import Logo from './assets/LichtBlickLogo.png';
import Home from './pages/Home';
import BookingEdit from './pages/BookingEdit';
import BookingWizard from './pages/BookingWizard';
import BookingBatch from './pages/BookingBatch';
import Booking from './pages/Booking';
import Person from './pages/Person';
// import Messaging from './pages/Messaging';
import MessageList from './pages/MessageList';
import MessageEdit from './pages/MessageEdit';
import { IconMoon, IconSun } from '@tabler/icons-react';
import PersonEdit from './pages/PersonEdit';
import { useIsAuthenticated } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import HttpClient from './helpers/HttpClient';
import { InvoiceSetting } from './types/invoice-setting.type';
import ContractList from './pages/ContractList';
import { useLanguage } from './providers/LanguageContext';
import { LANGUAGES } from './i18n/translations';
import ContractEdit from './pages/ContractEdit';
import Match from './pages/Match';
import { useUserContext } from "./providers/UserContext"; // adjust path as needed
import { USER_RIGHTS } from './constants/userRights';
import TagList from './pages/TagList';
import TagEdit from './pages/TagEdit';
// import TrainingSchedule from './pages/TrainingSchedule';
// import Teams from './pages/Teams';
// import TeamEdit from './pages/TeamEdit';
// import TrainingGrounds from './pages/TrainingGrounds';
// import TrainingGroundEdit from './pages/TrainingGroundEdit';
import BookingReport from './pages/BookingReport';
import BookingReportSummary from './pages/BookingReportSummary';
import ImportPayments from './pages/ImportPayments';
import Admin from './pages/Admin';
import Calculation from './pages/Calculation';
import MatchDataLoader from './pages/MatchDataLoader';
import PlayerIdMatching from './pages/PlayerIdMatching';
import MatchStats from './pages/MatchStats';
import ReceiptList from './pages/ReceiptList';
import ReceiptEdit from './pages/ReceiptEdit';
import BookingTypeList from './pages/BookingTypeList';
import BookingTypeEdit from './pages/BookingTypeEdit';
import TrainingEventList from './pages/TrainingEventList';
import TrainingEventDetail from './pages/TrainingEventDetail';
import TrainingSeriesCreate from './pages/TrainingSeriesCreate';
import ArticleList from './pages/ArticleList';
import ArticleEdit from './pages/ArticleEdit';
import InvoiceList from './pages/InvoiceList';
import InvoiceEdit from './pages/InvoiceEdit';
import RestaurantPOS from './pages/RestaurantPOS';
import RestaurantOrders from './pages/RestaurantOrders';
import InventoryList from './pages/InventoryList';
import InventoryArticleEdit from './pages/InventoryArticleEdit';
import StorageLocationList from './pages/StorageLocationList';
import RentalList from './pages/RentalList';
import AuditLog from './pages/AuditLog';
import TeamDraftList from './pages/TeamDraftList';
import TeamDraftDetail from './pages/TeamDraftDetail';
import PaymentAccountEdit from './pages/PaymentAccountEdit';
import MemberProfile from './pages/MemberProfile';
import MemberAccount from './pages/MemberAccount';
import CalendarSubscription from './pages/CalendarSubscription';
import Prae from './pages/Prae';
import InvoiceSettings from './pages/InvoiceSettings';
import MagicLinkVerify from './pages/MagicLinkVerify';
import { hasMagicLinkSession } from './helpers/TokenRefresh';

interface ProtectedRouteProps {
  requiredRight?: string | string[];
  staffOnly?: boolean;
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRight, staffOnly, children }) => {
  const { userRights } = useUserContext();
  const isExternal = userRights.includes(USER_RIGHTS.EXTERNAL);

  if (isExternal && staffOnly) return <Navigate to="/" replace />;
  if (requiredRight) {
    const rights = Array.isArray(requiredRight) ? requiredRight : [requiredRight];
    if (!rights.some((r) => userRights.includes(r))) return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export default function App() {
  const { userRightsLoading } = useUserContext();
  
  const { language, setLanguage } = useLanguage();
  const [opened, { toggle, close }] = useDisclosure();
  // Desktop sidebar can be hidden manually; the choice is remembered per browser.
  const [desktopNavOpened, setDesktopNavOpened] = useLocalStorage({
    key: 'desktop-nav-opened',
    defaultValue: true,
    getInitialValueInEffect: false,
  });
  const isMobile = useMediaQuery('(max-width: 48em)'); // matches Mantine's sm breakpoint
  const { setColorScheme } = useMantineColorScheme({
    keepTransitions: true,
  });


  const computedColorScheme = useComputedColorScheme('light');
  const isAuthenticated = useIsAuthenticated() || hasMagicLinkSession();
  const { inProgress } = useMsal();

  const [clubLogo, setClubLogo] = useState<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || inProgress !== 'none') return;
    new HttpClient<InvoiceSetting>('/invoice-setting').getSingle()
      .then(s => setClubLogo(s?.logoBase64 || null))
      .catch(() => {});
  }, [isAuthenticated, inProgress]);

  useEffect(() => {
    if (!clubLogo) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? document.createElement('link');
    link.rel = 'icon';
    link.href = clubLogo;
    if (!link.parentNode) document.head.appendChild(link);
  }, [clubLogo]);

  if (inProgress !== "none" || userRightsLoading) {
    return <div style={{ textAlign: "center", marginTop: "2rem" }}>Loading authentication...</div>;
  }

  return (

    <BrowserRouter>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 250,
          breakpoint: 'sm',
          collapsed: { mobile: !opened, desktop: !isAuthenticated || !desktopNavOpened },
        }}
        padding="md"
      >
        <AppShell.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '16px',
            paddingRight: '16px',
            flexWrap: 'nowrap',         // Prevent wrapping
            minWidth: 0,                // Allow shrinking
            gap: 8,                     // Optional: add some gap
          }}
        >         
          <Group style={{ minWidth: 0, flexShrink: 1, maxWidth: '100%' }} gap={8}>
            {isAuthenticated && <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Navigation ein-/ausblenden" />}
            {isAuthenticated && (
              <Burger
                opened={desktopNavOpened}
                onClick={() => setDesktopNavOpened((o) => !o)}
                visibleFrom="sm"
                size="sm"
                aria-label="Navigation ein-/ausblenden"
              />
            )}
            {clubLogo && (
              <img src={clubLogo} alt="Club logo" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
            )}
            <Text visibleFrom="sm">ClubManager</Text>
          </Group>
          <Group p="md" style={{ minWidth: 0, flexShrink: 1, flexWrap: 'nowrap' }}>
            <SegmentedControl
              size="xs"
              value={language}
              onChange={(v) => setLanguage(v as 'de' | 'en')}
              data={LANGUAGES}
            />
            <ActionIcon
              onClick={() => setColorScheme(computedColorScheme === 'light' ? 'dark' : 'light')}
              variant="default"
              size="xl"
              aria-label="Toggle color scheme"
            >
              {computedColorScheme === 'dark' && <IconSun stroke={1.5} />}
              {computedColorScheme === 'light' && <IconMoon stroke={1.5} />}
            </ActionIcon>
            <InstallAppButton />
            <Login />
          </Group>
        </AppShell.Header>
        {isMobile && opened && (
          <Overlay fixed onClick={close} backgroundOpacity={0.3} color="#000" zIndex={99} />
        )}
        <AppShell.Navbar p="md" style={isMobile && !opened ? { pointerEvents: 'none' } : undefined}>
          <Text hiddenFrom="sm">
            ClubManager
          </Text>
          <NavigationItems onNavigate={close} />
        </AppShell.Navbar>
        <AppShell.Main>
          <Container fluid style={ { width: '100%' } }>
            
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
              {inProgress === 'none' && isAuthenticated ? (
                <>
                  <Route
                    path="/booking"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <Booking />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking/report"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <BookingReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking/report/summary"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <BookingReportSummary />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/booking/edit/:id" element={
                    <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                      {React.createElement(WithIdParam(BookingEdit))}
                    </ProtectedRoute>
                  }
                  />
                  <Route path="/booking/create" element={
                    <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                      {<BookingEdit id={undefined}/>}
                    </ProtectedRoute>
                  }
                  />
                  <Route path="/booking/create" element={<BookingEdit id={undefined}/>} />
                  <Route path="/booking/wizard" element={<ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}><BookingWizard /></ProtectedRoute>} />
                  <Route path="/booking/batch-wizard" element={<ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}><BookingBatch /></ProtectedRoute>} />
                  <Route
                    path="/booking/import"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <ImportPayments />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/person" element={<ProtectedRoute requiredRight={USER_RIGHTS.PERSONS}><Person /></ProtectedRoute>} />
                  <Route path="/person/edit/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.PERSONS}>{React.createElement(WithIdParam(PersonEdit))}</ProtectedRoute>} />
                  <Route path="/person/create" element={<ProtectedRoute requiredRight={USER_RIGHTS.PERSONS}><PersonEdit id={undefined} /></ProtectedRoute>} />
                  <Route path="/contract" element={<ProtectedRoute requiredRight={USER_RIGHTS.CONTRACTS}><ContractList /></ProtectedRoute>} />
                  <Route path="/contract/create" element={<ProtectedRoute requiredRight={USER_RIGHTS.CONTRACTS}><ContractEdit id={undefined} /></ProtectedRoute>} />
                  <Route path="/contract/edit/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.CONTRACTS}>{React.createElement(WithIdParam(ContractEdit))}</ProtectedRoute>} />
                  {/* <Route path="/messaging" element={<Messaging />} /> */}
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.MESSAGES}>
                        <MessageList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages/create"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.MESSAGES}>
                        <MessageEdit id={undefined} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.MESSAGES}>
                        {React.createElement(WithIdParam(MessageEdit))}
                      </ProtectedRoute>
                    }
                  />
                  <Route path="match" element={<ProtectedRoute requiredRight={USER_RIGHTS.MATCHES}><Match /></ProtectedRoute>} />
                  <Route path="/match/stats" element={<ProtectedRoute requiredRight={USER_RIGHTS.MATCHES}><MatchStats /></ProtectedRoute>} />
                  <Route
                    path="/calculation"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <Calculation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matchdata"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.MATCHES}>
                        <MatchDataLoader />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/matchdata/player-ids"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.MATCHES}>
                        <PlayerIdMatching />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/tag" element={<ProtectedRoute requiredRight={USER_RIGHTS.TAGS}><TagList /></ProtectedRoute>} />
                  <Route path="/tag/create" element={<ProtectedRoute requiredRight={USER_RIGHTS.TAGS}><TagEdit id={undefined} /></ProtectedRoute>} />
                  <Route path="/tag/edit/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.TAGS}>{React.createElement(WithIdParam(TagEdit))}</ProtectedRoute>} />
                  <Route
                    path="/receipt"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <ReceiptList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/receipt/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        {React.createElement(WithIdParam(ReceiptEdit))}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookingtype"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <BookingTypeList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookingtype/create"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        <BookingTypeEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookingtype/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}>
                        {React.createElement(WithIdParam(BookingTypeEdit))}
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="/training" element={<TrainingSchedule />} />
                  <Route path="/training/grounds" element={<TrainingGrounds />} />
                  <Route path="/training/grounds/create" element={<TrainingGroundEdit id={undefined} />} />
                  <Route path="/training/grounds/edit/:id" element={React.createElement(WithIdParam(TrainingGroundEdit))} />
                  <Route path="/training/teams" element={<Teams />} />
                  <Route path="/training/teams/create" element={<TeamEdit id={undefined} />} />
                  <Route path="/training/teams/edit/:id" element={React.createElement(WithIdParam(TeamEdit))} /> */}
                  <Route path="/training/events" element={<ProtectedRoute requiredRight={USER_RIGHTS.TRAINING}><TrainingEventList /></ProtectedRoute>} />
                  <Route path="/training/events/create" element={<ProtectedRoute requiredRight={USER_RIGHTS.TRAINING}><TrainingEventDetail id={undefined} /></ProtectedRoute>} />
                  <Route path="/training/events/series/create" element={<ProtectedRoute requiredRight={USER_RIGHTS.TRAINING}><TrainingSeriesCreate /></ProtectedRoute>} />
                  <Route path="/training/events/edit/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.TRAINING}>{React.createElement(WithIdParam(TrainingEventDetail))}</ProtectedRoute>} />
                  <Route
                    path="/article"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        <ArticleList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/article/create"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        <ArticleEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/article/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        {React.createElement(WithIdParam(ArticleEdit))}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoice"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        <InvoiceList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoice/create"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        <InvoiceEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoice/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}>
                        {React.createElement(WithIdParam(InvoiceEdit))}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/restaurant"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.RESTAURANT}>
                        <RestaurantPOS />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/restaurant/orders"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.RESTAURANT}>
                        <RestaurantOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/restaurant/stock" element={<ProtectedRoute requiredRight={[USER_RIGHTS.RESTAURANT, USER_RIGHTS.INVENTORY]}><InventoryList restaurantOnly /></ProtectedRoute>} />
                  <Route path="/inventory" element={<ProtectedRoute requiredRight={USER_RIGHTS.INVENTORY}><InventoryList /></ProtectedRoute>} />
                  <Route path="/inventory/article/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.INVENTORY}><InventoryArticleEdit /></ProtectedRoute>} />
                  <Route path="/inventory/locations" element={<ProtectedRoute requiredRight={USER_RIGHTS.INVENTORY}><StorageLocationList /></ProtectedRoute>} />
                  <Route path="/inventory/rentals" element={<ProtectedRoute requiredRight={USER_RIGHTS.INVENTORY}><RentalList /></ProtectedRoute>} />
                  <Route path="/audit" element={<ProtectedRoute requiredRight={USER_RIGHTS.AUDIT}><AuditLog /></ProtectedRoute>} />
                  <Route path="/drafts" element={<ProtectedRoute requiredRight={USER_RIGHTS.DRAFTS}><TeamDraftList /></ProtectedRoute>} />
                  <Route path="/drafts/:id" element={<ProtectedRoute requiredRight={USER_RIGHTS.DRAFTS}><TeamDraftDetail /></ProtectedRoute>} />
                  <Route path="/prae" element={<ProtectedRoute requiredRight={USER_RIGHTS.ACCOUNTING}><Prae /></ProtectedRoute>} />
                  <Route path="/invoice/settings" element={<ProtectedRoute requiredRight={USER_RIGHTS.INVOICES}><InvoiceSettings /></ProtectedRoute>} />
                  <Route path="/member/profile" element={<MemberProfile />} />
                  <Route path="/member/account" element={<MemberAccount />} />
                  <Route path="/member/calendar" element={<CalendarSubscription />} />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ADMIN}>
                        <Admin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-account/new"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ADMIN}>
                        <PaymentAccountEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-account/edit/:id"
                    element={
                      <ProtectedRoute requiredRight={USER_RIGHTS.ADMIN}>
                        {React.createElement(WithIdParam(PaymentAccountEdit))}
                      </ProtectedRoute>
                    }
                  />
                </>
              ) : (
                <Route path="*" element={<Navigate to="/" replace />} />
              )}
           </Routes>
          </Container>
      </AppShell.Main>          
    </AppShell>      
  </BrowserRouter>
)}

function WithIdParam<T>(Component: React.ComponentType<T & { id?: number }>) {
  return function Wrapper(props: Omit<T, 'id'>) {
    const { id } = useParams<{ id?: string }>();
    const numericId = id ? Number(id) : undefined;
    return <Component {...(props as T)} id={numericId} />;
  };
}
